import { HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { VillaSearchTextItemType } from "@website/accommodation-ui/villa-search";
import { fetcher } from "@website/fetcher/fetcher";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { daysBetweenDates, today, tomorrow } from "@website/utils/date-format";
import { isSSR } from "@website/utils/is-ssr";
import { replaceOptions } from "@website/utils/replace-options";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { getCMSSeoContent } from "@website/website-ui/services/seo-contents";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import omitBy from "lodash-es/omitBy";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import type from "markdown";
import { GetServerSideProps } from "next";
import { filterXSS } from "xss";
import { ENDPOINTS } from "../../../constants";
import {
  HotelVillaRateDataType,
  HotelVillaReviewDataType,
  PersianVillaRatings
} from "../../room/[roomId]/index.types";
import {
  CityPagePropertiesType,
  VillaLocationResponseType,
  VillaSearchTextType
} from "./index.types";

export const transformHotelSchema = (
  data: VillaLocationResponseType,
  query: unknown
): HotelInfo => {
  const advertisementBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "advertisement"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const partnershipBadge = data?.room_data?.badges?.find(
    ({ type }) => type === "partnership"
  ) as VillaLocationResponseType["room_data"]["badges"][0];
  const ordinaryBadges = data?.room_data?.badges?.filter(
    ({ type }) => type === "ordinary"
  ) as VillaLocationResponseType["room_data"]["badges"];

  return {
    id: data.room_data.id,
    thumbnail: {
      alt: data.room_data.title,
      url: data.room_data?.image
        ? `${ENDPOINTS.CDN_IMAGES}${data.room_data.image}`
        : data.room_data?.property?.image
          ? `${ENDPOINTS.CDN_IMAGES}${data.room_data.property.image}`
          : ""
    },
    pricing: {
      basePriceOff: data?.room_availability?.base_price_off,
      basePrice: data?.room_availability?.base_price,
      price: data.room_availability.total_price,
      discountPercentage: 0,
      priceOff: data.room_availability.total_local_price_off
    },
    primaryBadge: {
      icon: "celebration" as const,
      color: "white",
      text: toString(partnershipBadge?.title),
      description: toString(partnershipBadge?.description),
      transparent: false
    },
    regularBadges:
      ordinaryBadges?.splice(0, 3)?.map(({ color, title }) => ({
        color,
        text: title,
        transparent: true
      })) ?? [],
    advertisementBadge: {
      color: toString(advertisementBadge?.color),
      text: toString(advertisementBadge?.title),
      transparent: false
    },
    hotelDetail: {
      cityId: data.room_data.property.city.id,
      address: [
        data.room_data.property.village?.title,
        data.room_data.property.state?.title,
        data.room_data.property.city?.title
      ]
        .filter(Boolean)
        .join(", "),
      isAvailable: data.room_data.is_active,
      hotelTitle: data.room_data.title || data.room_data.property.title || "",
      stars: 0,
      rating: data?.room_data?.property?.rating?.avg,
      reviewCount: data?.room_data?.property?.rating?.count,
      roomTitle: `${data.room_data.capacity} نفر پایه ${
        data.room_data?.extra_capacity > 0
          ? `+ ${data.room_data?.extra_capacity} نفر اضافه`
          : ""
      }`,
      roomCount: 0,
      cityTitle: data.room_data?.property?.city?.title || "",
      propertyId: data.room_data?.property?.id || 0,
      isInstant: data?.room_availability?.instant || false,
      hotelPageUrl: {
        pathname: "/room/[roomId]",
        query: {
          roomId: data.room_data.id || "",
          ...(query["room_capacity"] > data?.room_data?.capacity
            ? {
                extra_capacity:
                  Number(query["room_capacity"]) -
                    Number(data?.room_data?.capacity) || ""
              }
            : {})
        }
      }
    },
    reservationInfo: {
      bedCount: get(query, "room_capacity") || 0,
      nightCount:
        daysBetweenDates(get(query, "date_from"), get(query, "date_to")) || 1
    },
    location: {
      lat: data.room_data?.property?.location?.latitude,
      lng: data.room_data?.property?.location?.longitude
    },
    gallery:
      data?.room_data?.gallery
        ?.map((item, index) => ({
          id: index + 1,
          title: item?.title,
          media: `${ENDPOINTS.CDN_IMAGES}${item?.name}`,
          order: item?.order_no || 0
        }))
        .sort((a, b) => a.order - b.order) ||
      []?.splice(0, 5) ||
      [],
    categories: data?.room_data?.categories || []
  };
};

export const fetchPropertyDynamicFilters = (
  geoType: string,
  slug: string,
  date_from: string,
  date_to: string,
  geo: boolean,
  nearby?: boolean
) => {
  // TODO: improve this (even with undefined react-query is calling fetcher)
  if (geoType === "undefined" || slug === "undefined") return;

  return fetcher({
    url: ENDPOINTS.VILLA_PROPERTY_DYNAMIC_FILTERS,
    params: {
      slug,
      geoType,
      geo,
      nearby: nearby || false,
      ...(date_from !== "undefined" && { from: date_from }),
      ...(date_to !== "undefined" && { to: date_to })
    },
    method: "GET"
  })
    .then(({ data }) => ({
      accommodations: data?.accommodations?.map((accommodation) => ({
        label: accommodation?.title,
        type: accommodation?.type
      })),
      categories: data?.categories?.map((category) => ({
        id: category?.id,
        title: category?.name
      })),
      facilities: data?.amenities?.map((facility) => ({
        id: facility?.id,
        title: facility?.title,
        icon: facility?.icon
      })),
      rates: data?.rates?.reverse() || [],
      priceRange: {
        min: data?.price_range?.min ?? 100_000,
        max: data?.price_range?.max ?? 1_550_000
      },
      geo_property_summary: data?.geo_property_summary?.map((geo) => ({
        geo_slug: geo?.geo_slug,
        geo_type: geo?.geo_type,
        property_count: geo?.property_count,
        title: geo?.title
      })),
      nearby: data?.nearby
    }))
    .catch(() => ({
      accommodations: [],
      categories: [],
      facilities: [],
      rates: [],
      priceRange: {
        min: 100_000,
        max: 1_550_000
      },
      geo_property_summary: [],
      nearby: false
    }));
};

export const fetchBannersData = (page: string, type: string) =>
  fetcher({
    url: ENDPOINTS.GET_BANNERS,
    method: "GET",
    params: {
      channel: getChannel() === "website" ? "web" : getChannel(),
      product: "villa-domestic",
      page,
      type
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.GET_BANNERS, page, type]
    }
  })
    .then(({ data }) => data)
    .catch(() => []);

export const fetchVillagesData = (_query: unknown) => {
  const query = isSSR() ? pick(_query, ["locationType", "cityTitle"]) : _query;
  const url = replaceOptions(ENDPOINTS.VILLA_SEARCH_LOCATION, {
    cityTitle: get(query, "cityTitle"),
    locationType: get(query, "locationType")
  });

  const categories = toString(get(query, "categories")).split(",");
  const category_ids = categories.filter((item) => item !== "instant");
  const hasInstant = categories.includes("instant");
  const geo_property_summary = toString(
    get(query, "geo_property_summary")
  ).split(",");
  const geoTypes = [];
  const geoSlugs = [];
  if (geo_property_summary)
    for (const item of geo_property_summary) {
      if (item) {
        const [slug, geo_type] = item.split("___");
        geoSlugs.push(slug);
        geoTypes.push(geo_type);
      }
    }

  const parameters = omitBy(
    {
      from: get(query, "date_from") || today(),
      to: get(query, "date_to") || tomorrow(),
      priceGT: get(query, "price_from"),
      priceLT: get(query, "price_to"),
      type: get(query, "type"),
      instant: hasInstant,
      availability: get(query, "room_count") || 1,
      nearby: get(query, "nearby") || false,
      version: get(query, "version") || "room",
      capacity: get(query, "room_capacity") || 1,
      page: toNumber(get(query, "page") || 1),
      q: get(query, "q"),
      amenities: toString(get(query, "facilities")).replaceAll("-", ","),
      rate: get(query, "rate") === "all" ? "" : get(query, "rate"),
      ...(get(query, "order_by") ? { order: get(query, "order_by") } : {}),
      category_ids: category_ids.join(","),
      channel: getChannel(),
      geo_slug: geoSlugs.join(","),
      geo_type: geoTypes.join(",")
    },
    (value) => !value
  );
  return fetcher({
    method: "GET",
    url,
    params: parameters,
    next: {
      revalidate: 300,
      tags: [url, JSON.stringify(parameters)]
    }
  })
    .then(
      ({ data }) =>
        (data?.data?.map((item) => ({
          ...transformHotelSchema(item, query),
          count: toNumber(data.count || 0),
          total: toNumber(data.total || 0),
          page: toNumber(get(query, "page") || 1)
        })) ?? []) as CityPagePropertiesType["villagesData"]
    )
    .catch(() => []);
};

export const fetchLocationData = (locationType: string, cityTitle: string) => {
  const urls = {
    city: ENDPOINTS.SEARCH_CITY_SLUG,
    state: ENDPOINTS.SEARCH_STATE_SLUG,
    village: ENDPOINTS.SEARCH_VILLAGE_SLUG,
    area: ENDPOINTS.SEARCH_AREA_SLUG,
    category: ENDPOINTS.SEARCH_CATEGORY_SLUG
  };

  if (!urls[locationType]) {
    return {
      id: -1,
      url: "",
      title: "",
      location: null
    };
  }

  return fetcher({
    url: urls[locationType],
    options: {
      slug: cityTitle
    },
    next: {
      revalidate: 300,
      tags: [urls[locationType], cityTitle]
    }
  })
    .then(({ data }) => ({
      id: data?.id ?? "",
      url: data?.slug ?? "",
      title: data?.title ?? "",
      location: data?.location || {},
      state: data?.state ?? {},
      type: locationType
    }))
    .catch(() => ({
      id: -1,
      url: "",
      title: "",
      location: null
    }));
};

export const fetchSearchTextData = (q: string) =>
  fetcher({
    url: ENDPOINTS.CITY_AUTOCOMPLETE,
    params: {
      q: q?.replaceAll(/[!"#$%&()*,.:<>?@^{|}]/g, "")
    }
  }).then(({ data }) => {
    const result = data as VillaSearchTextType;
    return (result?.geo || []).map(
      (item) =>
        ({
          id: toString(item?.id),
          title: item?.title,
          url: item?.slug,
          hotel_count:
            result?.properties?.filter(
              ({ city_id, village_id, state_id }) =>
                (item?.entity === "VILLAGE" && item.id === village_id) ||
                (item?.entity === "CITY" && item.id === city_id) ||
                (item?.entity === "STATE" && item.id === state_id)
            ).length || 0,
          info: "",
          stars: 0,
          isHotel: false,
          subtitle: item.entity,
          type: item?.entity?.toLowerCase(),
          location: item?.location
        }) as VillaSearchTextItemType
    );
  });

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: ENDPOINTS.INSTALMENT_WALLET,
    method: "GET",
    headers: {
      product: "VILLA"
    }
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));

export const fetchHotelRateData = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_RATE,
    options: { hotelId },
    method: "GET",
    headers: {
      product: "villa"
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.HOTEL_RATE, toString(hotelId)]
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaRateDataType;
    return {
      rateDetail: {
        clean_rate: _data?.cleaning || "",
        facility_rate: _data?.facilities || "",
        food_quality_rate: _data?.match || "",
        location_rate: _data?.location || "",
        staff_rate: _data?.friendly || "",
        value_for_money_rate: _data?.priceQuality || ""
      },
      rate: Number(_data?.overall).toFixed(1) ?? "",
      rateCount: _data?.rateCount ?? 0,
      offerCount: _data?.recommendationCount ?? 0,
      offerMessage:
        _data?.recommendationType === "recommendation"
          ? "۷۵ درصد از میهمانان اخیر، این اقامتگاه رو پیشنهاد کرده اند. "
          : _data?.recommendationType === "fullRate"
            ? "میهمانان در ۵ رزرو اخیر، امتیاز کامل را به اقامتگاه داده اند"
            : _data?.recommendationType
              ? `میهمانان در ۵ رزرو اخیر، به  "${PersianVillaRatings?.[
                  _data?.recommendationType
                ]}" امتیاز کامل داده اند.`
              : "",
      aiSummary: type?.markdown?.toHTML(data?.aiSummary)
    };
  });

export const fetchHotelReviewData = (
  hotelId: unknown,
  page: unknown,
  product: string,
  filters: Record<string, string>
) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW, { hotelId }),
    method: "GET",
    headers: {
      product
    },
    params: {
      ...filters,
      pageSize: 5,
      page
    }
  }).then(({ data }) => {
    const _data = data as HotelVillaReviewDataType;
    return {
      page: toNumber(_data?.page),
      total: _data?.total,
      rateReviews: (_data?.result ?? [])?.map((comment) => ({
        id: comment?.id,
        helpfulSubmitted: comment?.helpfulSubmitted,
        userName: comment?.passenger,
        roomName: comment.roomTitle,
        rate: String(comment?.avgRating || 0),
        registeredDate: comment?.createdAt,
        standNights: comment?.dateCount,
        comment:
          replaceSnappTripBasedOnChannel(
            filterXSS(comment?.comment, {
              whiteList: {},
              stripIgnoreTag: true,
              stripIgnoreTagBody: ["script"]
            })
          ) ?? "",
        strengths: comment?.pros?.filter(Boolean)?.join(" ,") ?? "",
        weakness: comment?.cons?.filter(Boolean)?.join(" ,") ?? "",
        pictures: comment?.images?.map((image, index) => ({
          id: index,
          media: `${process.env["NEXT_PUBLIC_BASE_URL_IMAGES"]}/reviews/${image}`,
          title: ""
        }))
      }))
    };
  });

export const getServerSideProps: GetServerSideProps<
  CityPagePropertiesType
> = async ({ query, res }) => {
  const locationData = await fetchLocationData(
    String(query?.locationType),
    String(query.cityTitle)
  );

  if (!locationData?.id) {
    res.statusCode = 410;
    return { props: {} };
  }

  const seoData = await (isChannel(["website", "web", "snapp-ir"])
    ? getCMSSeoContent(
        `${process.env.NEXT_PUBLIC_BASE_PATHNAME}/${query?.locationType}/${query?.cityTitle}`
      )
    : Promise.resolve(null));

  if (seoData?.redirect) {
    return { redirect: { destination: seoData?.redirect, permanent: true } };
  }

  const [villagesData, smallBanners, staticBanners] = await Promise.all([
    isEmpty(omit(query, ["locationType", "cityTitle"]))
      ? fetchVillagesData(query)
      : Promise.resolve([]),
    fetchBannersData("Citypage", "small"),
    fetchBannersData("Citypage", "static")
  ]);

  return {
    props: {
      seoData: seoData || null,
      locationData,
      villagesData,
      smallBanners,
      staticBanners
    }
  };
};
